import React, { useState } from 'react';
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  Container,
  Paper,
  Link,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import UserDetailsForm from './UserDetailsForm';
import CompanyDetailsForm from './CompanyDetailsForm';
import LoadingRedirect from './LoadingRedirect'; // Import our new component
import { checkEmailExists, submitUserProfile, submitCompanyDetails } from '../../api';
import Logo from '../Logo.js';
import Footer from '../Footer.js';

// Updated steps to include the loading/redirect page
const steps = ['Profile Setup', 'Company Details', 'Finalizing'];

const TenantOnboardingForm = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [leadId, setLeadId] = useState(null);
  const [userData, setUserData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    role: 'Executive',
    waPlan: 'WhatsApp Lite'
  });
  
  const [companyData, setCompanyData] = useState({
    companyName: '',
    industry: 'Technology',
    size: '',
    website: '',
    country: '',
    gst: '',
    primaryFocus: 'B2B (Business To Business)',
    hearAboutUs: 'Search Engine'
  });
  
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const handleUserDataChange = (field, value) => {
    setUserData({
      ...userData,
      [field]: value
    });
    
    // Clear error for this field when user types
    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: null
      });
    }
  };

  const handleCompanyDataChange = (field, value) => {
    // If changing country to something other than India, clear GST
    if (field === 'country' && value !== 'India') {
      setCompanyData({
        ...companyData,
        [field]: value,
        gst: '' // Clear GST when country is not India
      });
    } else {
      setCompanyData({
        ...companyData,
        [field]: value
      });
    }
    
    // Clear error for this field when user types
    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: null
      });
    }
  };

  const validateUserDetails = async () => {
    const newErrors = {};
    
    // Basic validation
    if (!userData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!userData.lastName.trim()) newErrors.lastName = 'Last name is required';
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!userData.email.trim()) {
      newErrors.email = 'Work email is required';
    } else if (!emailRegex.test(userData.email)) {
      newErrors.email = 'Please enter a valid email';
    } else {
      // Check if email already exists in the system
      try {
        setLoading(true);
        const exists = await checkEmailExists(userData.email);
        
        if (exists) {
          newErrors.email = 'Looks like you are already a BotBridge user. Please login';
          newErrors.emailAction = 'login';
        }
      } catch (error) {
        console.error('Error checking email:', error);
      } finally {
        // setLoading(false);
      }
    }
    
    if (!userData.phone.trim()) newErrors.phone = 'Contact phone number is required';
    if (!userData.role) newErrors.role = 'Your role is required';
    if (!userData.waPlan) newErrors.waPlan = 'WA Plan is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateCompanyDetails = () => {
    const newErrors = {};
    
    if (!companyData.companyName.trim()) newErrors.companyName = 'Company name is required';
    if (!companyData.industry) newErrors.industry = 'Industry is required';
    if (!companyData.size) newErrors.size = 'Company size is required';
    
    // Updated URL validation logic - REQUIRES proper domain with TLD
    if (!companyData.website.trim()) {
      newErrors.website = 'Company website is required';
    } else {
      // Clean and normalize the URL
      let websiteUrl = companyData.website.trim();
      
      // If no protocol is provided, assume https://
      if (!websiteUrl.match(/^https?:\/\//i)) {
        websiteUrl = 'https://' + websiteUrl;
      }
      
      // More comprehensive URL validation
      try {
        const url = new URL(websiteUrl);
        
        // STRICTER domain validation - requires at least one dot (TLD)
        const strictDomainRegex = /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;
        
        if (!strictDomainRegex.test(url.hostname)) {
          // newErrors.country = 'Country location is required';
          newErrors.website = 'Please enter a valid website URL (e.g., company.com)';
        }
      } catch (error) {
        newErrors.website = 'Please enter a valid website URL';
      }
    }
    
    if (!companyData.country) newErrors.country = 'Country location is required';
    
    // GST validation only for India
    if (companyData.country === 'India') {
      const gstRegex = /\d{2}[A-Z]{5}\d{4}[A-Z]{1}[A-Z\d]{1}[Z]{1}[A-Z\d]{1}/;
      // Check for all zeros
      const allZerosRegex = /^0+$/;
      
      if (!companyData.gst.trim()) {
        // newErrors.gst = 'GST is required for Indian companies';
        console.log("GST is not required for Indian companies");
      } else if (!gstRegex.test(companyData.gst)) {
        newErrors.gst = 'Please enter a valid Indian GST number';
      } else if (allZerosRegex.test(companyData.gst.replace(/[^0-9]/g, ''))) {
        newErrors.gst = 'GST number cannot contain all zeros in numeric portions';
      }
    } 
    
    if (!companyData.primaryFocus) newErrors.primaryFocus = 'Primary focus is required';
    if (!companyData.hearAboutUs) newErrors.hearAboutUs = 'This field is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = async () => {
    if (activeStep === 0) {
      // setLoading(true);
      const isValid = await validateUserDetails();
      
      if (isValid) {
        // First submit the user details to the backend
        try {
          const response = await submitUserProfile(userData);
          
          if (response.success) {
            // Store the lead ID for the next step
            setLeadId(response.lead_id);
            setActiveStep((prevStep) => prevStep + 1);
          } else {
            setSnackbar({
              open: true,
              message: response.error || 'An error occurred while saving your profile',
              severity: 'error'
            });
          }
        } catch (error) {
          console.error('Error in profile submission:', error);
          setSnackbar({
            open: true,
            message: 'An unexpected error occurred',
            severity: 'error'
          });
        }
      }
      setLoading(false);
    } else if (activeStep === 1) {
      const isValid = validateCompanyDetails();
      if (isValid) {
        handleSubmit();
      }
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleSubmit = async () => {
    setLoading(true);
    // setLoading(false);
    
    try {
      // Submit company details to the backend
      const response = await submitCompanyDetails(companyData, leadId);
      
      if (response.success) {
        // Move to the loading/redirect step
        setActiveStep(2);
      } else {
        setSnackbar({
          open: true,
          message: response.error || 'An error occurred during submission',
          severity: 'error'
        });

        // Check if error is gst or website
        if (response.gst) {
          setErrors({
            ...errors,
            gst: response.gst
          });
        }
        else if (response.website) {
          setErrors({
            ...errors,
            website: response.website
          });
        }
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSnackbar({
        open: true,
        message: 'An unexpected error occurred',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <UserDetailsForm 
            userData={userData} 
            errors={errors}
            onChange={handleUserDataChange} 
          />
        );
      case 1:
        return (
          <CompanyDetailsForm 
            companyData={companyData} 
            errors={errors}
            onChange={handleCompanyDataChange} 
          />
        );
      case 2:
        // This is our new loading/redirect step
        return <LoadingRedirect leadId={leadId} />;
      default:
        return 'Unknown step';
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <Logo />
        </Box>
        <Typography variant="h5" align="center" gutterBottom>
          Create Account
        </Typography>
        <hr />
        <Stepper activeStep={activeStep} sx={{ pt: 3, pb: 5 }}>
          {steps.map((label, index) => (
            <Step key={label}>
              <StepLabel 
                StepIconProps={{
                  style: {
                    color: activeStep >= index ? '#1976d2' : '#bdbdbd',
                  }
                }}
                style={{
                  color: activeStep >= index ? '#1976d2' : '#bdbdbd',
                }}
              >
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
        
        {getStepContent(activeStep)}
        
        {/* Only show navigation buttons for steps 0 and 1 */}
        {activeStep < 2 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3 }}>
            {/* Left side with login link */}
            <Button 
              variant="text" 
              color="primary" 
              component={Link} 
              href="/login" 
              sx={{ textTransform: 'none' }}
              disabled={loading}
            >
              Already have an account? Login
            </Button>
            
            {/* Right side with existing navigation buttons */}
            <Box sx={{ display: 'flex' }}>
              {activeStep !== 0 && (
                <Button 
                  onClick={handleBack} 
                  sx={{ mr: 1 }}
                  disabled={loading}
                >
                  Back
                </Button>
              )}
              
              <Button
                variant="contained"
                color="primary"
                onClick={handleNext}
                disabled={
                  loading ||
                  (activeStep === 0 && (
                    !userData.firstName || 
                    !userData.lastName || 
                    !userData.email || 
                    !userData.phone || 
                    !userData.role || 
                    !userData.waPlan
                  )) || 
                  (activeStep === 1 && (
                    !companyData.companyName || 
                    !companyData.website ||
                    !companyData.industry ||
                    !companyData.size ||
                    !companyData.country ||
                    // GST is only required for India
                    // (companyData.country === 'India' && !companyData.gst) ||
                    !companyData.primaryFocus ||
                    !companyData.hearAboutUs
                  ))
                }
                sx={{
                  bgcolor: (activeStep === 0 && (
                    !userData.firstName || 
                    !userData.lastName || 
                    !userData.email || 
                    !userData.phone || 
                    !userData.role || 
                    !userData.waPlan
                  )) || 
                  (activeStep === 1 && (
                    !companyData.companyName || 
                    !companyData.website ||
                    !companyData.industry ||
                    !companyData.size ||
                    !companyData.country ||
                    // GST is only required for India
                    // (companyData.country === 'India' && !companyData.gst) ||
                    !companyData.primaryFocus ||
                    !companyData.hearAboutUs
                  )) ? '#bdbdbd' : '#1976d2'
                }}
                startIcon={loading && <CircularProgress size={20} color="inherit" />}
              >
                {activeStep === steps.length - 2 ? 'Create Account' : 'Continue'}
              </Button>
            </Box>
          </Box>
        )}
        <Footer />
      </Paper>
      
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={() => setSnackbar({...snackbar, open: false})}
      >
        <Alert onClose={() => setSnackbar({...snackbar, open: false})} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default TenantOnboardingForm;